import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import SectionTitle from "./SectionTitle";
import { ArrowUpRight } from "lucide-react";
import { useSiteServicesData } from "../hooks/useSiteData";
import { getServicePreviewText } from "../lib/html";
import { type, typeRichDescription } from "../lib/typography";

function ServiceCard({ service, index }) {
  const Icon = Icons[service.icon] || Icons.Hammer;
  const preview =
    getServicePreviewText(service.description, service.title) || service.short || "";
  const number = String(service.position ?? index + 1).padStart(2, "0");

  return (
    <Link
      to={`/services/${service.id}`}
      className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-[#1e6fcf]/60 hover:shadow-2xl hover:shadow-[#1e6fcf]/10 hover:-translate-y-1 transition-all duration-500"
    >
      <div className="relative h-52 overflow-hidden bg-[#0a1a2f]">
        {service.image ? (
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a2f] via-[#0a1a2f]/30 to-transparent" />
        <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-[#1e6fcf] shadow-lg shadow-[#1e6fcf]/30 flex items-center justify-center ring-2 ring-white/20">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="absolute top-4 right-4 text-xs font-mono text-white/80 tracking-widest px-2 py-1 rounded bg-black/25 backdrop-blur-sm">
          {number}
        </div>
      </div>
      <div className="p-6">
        <h3 className={`${type.cardTitle} text-[#1e6fcf] group-hover:text-[#1758a8] transition-colors`}>
          {service.title}
        </h3>
        {preview && (
          <p className={`mt-2 ${typeRichDescription} line-clamp-3`}>
            {preview}
          </p>
        )}
        <div className="mt-5 flex items-center gap-2 text-[#1e6fcf] font-bold text-xs uppercase tracking-wider">
          Learn More
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function ServicesGrid({ limit }) {
  const { data: servicesPage } = useSiteServicesData();
  const services = servicesPage?.items || [];

  const list = limit ? services.slice(0, limit) : services;

  if (!servicesPage || services.length === 0) return null;

  return (
    <section className="relative py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <SectionTitle
            eyebrow={servicesPage.sectionTagline}
            title={servicesPage.mainTitle}
            subtitle={servicesPage.subText}
          />
          <Link
            to="/services"
            className="hidden items-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-[#0a1a2f] shadow-sm transition-all duration-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white lg:inline-flex"
          >
            View All Services
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
